import { defineStore } from 'pinia';
import { DateTime } from 'luxon';
import { convertStorageUnits } from '@base-shared/utils';
import { handleRequest } from '@base-shared/utils/request-handler';
import { useUserStore } from '@base-shared/stores/user';
import { useGroupApi, useUserApi } from '@base-shared/composables/use-api';
import {
  PRODUCT_ARYA,
  PRODUCT_ASSETS,
  APP,
  USER_RIGHTS,
  ARYA_PLANS,
  KB,
  GB,
  FEATURE_SET_TYPES
} from '@base-shared/utils/constants';
import type { components } from '@base-shared/types/group.openapi';

type GroupSchema = components['schemas'];

export type GroupUser = GroupSchema['UserItem'];
export type GroupInfo = GroupSchema['GetGroupInfoResponse'];
export type Product = GroupSchema['Product'];

export type GroupUserRights = GroupSchema['UserModifiableRights'][];

export type GroupPlanQuota = {
  used: number;
  max: number;
};

export type SearchableGroupUser = GroupUser & {
  fullName: string;
};

interface GroupState {
  users: GroupUser[];
  allowableRights: string[];
  allowableProducts: string[];
  isLoadingUsers: boolean;

  groupInfo: GroupInfo | null;
  isLoadingGroupInfo: boolean;
  isSilentUpdatingAccountName: boolean;
};

export const useGroupStore = defineStore('group', {
  state: (): GroupState => ({
    users: [],
    allowableRights: [],
    allowableProducts: [],
    isLoadingUsers: false,

    groupInfo: null,
    isLoadingGroupInfo: false,
    isSilentUpdatingAccountName: false
  }),
  getters: {
    name: (state) => state.groupInfo?.accountName ?? state.groupInfo?.address?.companyName,
    isAryaMaxedUsers: (state) => {
      if (state.isLoadingUsers || state.isLoadingGroupInfo) {
        return false;
      }

      const maxUsers = state.groupInfo?.activePlan?.maxUsers;
      if (typeof maxUsers !== 'number') {
        return false; // Unlimited users
      }

      const aryaUsers = state.users.filter((user) => user.products && user.products.includes(PRODUCT_ARYA));
      return aryaUsers.length >= maxUsers;
    },
    isCurrentUserTheOnlyGlobalAdmin: (state) => {
      if (state.isLoadingUsers || state.isLoadingGroupInfo) {
        return false;
      }

      const currentUser = useUserStore().currentUser;
      if (!currentUser) return false;

      const globalAdmins = state.users.filter((user) => !!user.rights?.includes(USER_RIGHTS.GLOBAL_ADMIN));

      return globalAdmins.length === 1 && globalAdmins[0]?.userId === currentUser.userId;
    },

    hasMultipleUsers: (state) => state.users.length > 1,
    isAryaPlanDiscovery: (state) => {
      return state.groupInfo?.activePlan?.name === ARYA_PLANS.DISCOVERY;
    },
    isAryaPlanPremium: (state) => {
      return state.groupInfo?.activePlan?.name === ARYA_PLANS.PREMIUM;
    },
    isAryaPlanEnterprise: (state) => {
      const activePlanName = state.groupInfo?.activePlan?.name;
      if (!activePlanName) return false;

      const enterprisePlans: string[] = [
        ARYA_PLANS.ENTERPRISE,
        ARYA_PLANS.ENTERPRISE_WIDGETS,
        ARYA_PLANS.ENTERPRISE_KIOSK,
        ARYA_PLANS.ENTERPRISE_SYSTEMS
      ];

      return enterprisePlans.includes(activePlanName);
    },
    isAryaPlanEnterpriseDemo: (state) => {
      const activePlanName = state.groupInfo?.activePlan?.name;
      if (!activePlanName) return false;

      // For cases where the plan name starts with 'enterprise' and ends with '-demo')
      // it is also considered as Enterprise Demo
      return activePlanName.startsWith(ARYA_PLANS.ENTERPRISE) && activePlanName.endsWith('-demo');
    },
    isAryaPlanEnterpriseTrial: (state) => {
      const activePlanName = state.groupInfo?.activePlan?.name;
      if (!activePlanName) return false;

      return activePlanName === ARYA_PLANS.ENTERPRISE_TRIAL;
    },
    isAryaPlanSuspended: (state) => {
      return !!state.groupInfo?.activePlan?.suspended;
    },
    isAryaPlanExpired: (state) => {
      if (!state.groupInfo?.activePlan?.expiryDate) return false;
      // Always refer to UTC Midnight for expiration comparison
      const today = DateTime.utc().startOf('day');
      const expiryDate = DateTime
        .fromISO(state.groupInfo.activePlan.expiryDate, { zone: 'utc' })
        .startOf('day');

      return expiryDate < today;
    },
    aryaPlanNameDisplay: (state) => {
      return getAryaPlanName(state.groupInfo?.activePlan?.name ?? '');
    },
    isGroupCanBeDeleted() {
      const hasDevelopmentRights = useUserStore().hasDevelopmentRights;

      // If the account has more than one user, it cannot be deleted
      if (this.hasMultipleUsers) return false;

      // If the account has only 1 user, and an Arya Enterprise,
      // it can only be deleted if the user has development rights
      if (this.isAryaPlanEnterprise) {
        return hasDevelopmentRights;
      }

      // To all other cases, if user is the last user and only
      // global admin, it can delete.
      // This can apply to Discovery, Premium, Enterprise Demo
      // or Enterprise Trial.
      if (this.isCurrentUserTheOnlyGlobalAdmin) return true;

      return false;
    },
    hasGeneratedApiKey: (state) => {
      return !!state.groupInfo?.hasAPIKey;
    },
    /**
     * Get the active plan expiration date from group info.
     */
    aryaPlanExpirationDate(): string | null {
      if (!this.groupInfo?.activePlan?.expiryDate) return null;

      // NOTE: that date supplied from the API is without time and timezone information, but we know it's UTC
      const expiryDate = DateTime.fromISO(this.groupInfo.activePlan.expiryDate, { zone: 'utc' }).startOf('day');
      if (!expiryDate.isValid) return null;
      return expiryDate.toISO();
    },
    /**
     * Get the active plan expiration date as a Luxon DateTime object.
     * If there is no active plan or the expiry date is invalid, return null.
     */
    aryaPlanExpirationDateTime(): DateTime<true> | null {
      if (!this.groupInfo?.activePlan?.expiryDate) return null;

      // NOTE: that date supplied from the API is without time and timezone information, but we know it's UTC
      const expiryDate = DateTime.fromISO(this.groupInfo.activePlan.expiryDate, { zone: 'utc' }).startOf('day');

      if (!expiryDate.isValid) return null;
      return expiryDate;
    },
    /**
     * Get the active plan expiration date formatted in a medium format (e.g., Jan 1, 2024).
     * If there is no active plan or the expiry date is invalid, return null.
     * Uses Luxon's DATE_MED format.
     */
    aryaPlanExpirationDateMedFormat(): string | null {
      return this.aryaPlanExpirationDateTime?.isValid
        ? this.aryaPlanExpirationDateTime.toLocaleString(DateTime.DATE_MED).toUpperCase()
        : null;
    },
    /**
     * Get the active plan expiration date countdown in months and days.
     * If there is no active plan or the expiry date is invalid, return null.
     * Uses Luxon's DateTime and Duration for calculation.
     */
    aryaPlanExpirationDateTimeCountdown(): { inMonths: number; inDays: number } | null {
      if (!this.aryaPlanExpirationDateTime) return null;
      // Always refer to UTC Midnight for expiration comparison
      const today = DateTime.utc().startOf('day');
      const expiryDate = this.aryaPlanExpirationDateTime.startOf('day');
      const diff = expiryDate.diff(today, ['months', 'days']);
      const inMonths = Math.floor(diff.months);
      const inDays = Math.floor(diff.days);
      return { inMonths, inDays };
    },

    /**
     * Get the active plan players quota (used and max).
     * For Discovery plan, `max` will be unlimited (`Infinity`).
     */
    aryaPlanPlayersQuota(): GroupPlanQuota | null {
      if (!this.groupInfo) return null;
      const maxPlayers = this.groupInfo?.activePlan?.maxPlayers;

      const used = this.groupInfo.usage?.numberOfPlayers ?? 0;
      const max = (this.isAryaPlanDiscovery || this.isAryaPlanPremium) && !maxPlayers
        ? Infinity
        : maxPlayers ?? 0;

      return { used, max };
    },
    /**
     * Get the active plan storage quota (used and max) in KB.
     */
    aryaPlanStorageKBQuota(): GroupPlanQuota | null {
      if (!this.groupInfo) return null;
      const used = this.groupInfo.usage?.usedStorageKB ?? 0;
      const max = this.groupInfo.activePlan?.maxStorageKB ?? 0;

      return { used, max };
    },
    /**
     * Get the active plan storage quota (used and max) in GB.
     */
    aryaPlanStorageGBQuota(): GroupPlanQuota | null {
      if (!this.aryaPlanStorageKBQuota) return null;
      const usedInGB = Number(convertStorageUnits(this.aryaPlanStorageKBQuota.used, KB, GB).toFixed(1));
      const maxInGB = Number(convertStorageUnits(this.aryaPlanStorageKBQuota.max, KB, GB).toFixed(1));

      return { used: usedInGB, max: maxInGB };
    },
    /**
     * Get the active plan user quota (used and max).
     * For Enterprise plan, `max` will be unlimited (`Infinity`).
     */
    aryaPlanUserQuota(): GroupPlanQuota | null {
      if (!this.groupInfo) return null;
      const maxUsers = this.groupInfo?.activePlan?.maxUsers;

      const used = this.groupInfo.usage?.numberOfUsers ?? 0;
      const max = this.isAryaPlanEnterprise && !maxUsers
        ? Infinity
        : this.groupInfo.activePlan?.maxUsers ?? 0;

      return { used, max };
    },

    /**
     * Get the feature sets plan expiration date from group info.
     */
    featureSetExpirationDate(): string | null {
      return this.groupInfo?.featureSets?.expirationDate ?? null;
    },
    /**
     * Get the feature sets plan expiration date as a Luxon DateTime object.
     * If there is no active plan or the expiry date is invalid, return null.
     */
    featureSetExpirationDateTime(): DateTime<true> | null {
      if (!this.groupInfo?.featureSets?.expirationDate) return null;

      // for consistency with other expiration date getters, use startOf('day')
      const expiryDate = DateTime.fromISO(this.groupInfo.featureSets.expirationDate, { zone: 'utc' }).startOf('day');

      if (!expiryDate.isValid) return null;
      return expiryDate;
    },
    /**
     * Get the feature sets plan expiration date formatted
     * in a medium format (e.g., Jan 1, 2024). If there is
     * no feature set plan or the expiry date is invalid,
     * return null.
     * Uses Luxon's DATE_MED format.
     */
    featureSetExpirationDateMedFormat(): string | null {
      return this.featureSetExpirationDateTime?.isValid
        ? this.featureSetExpirationDateTime.toLocaleString(DateTime.DATE_MED).toUpperCase()
        : null;
    },
    /**
     * Get the feature sets plan expiration date countdown in months and days.
     * If there is no active plan or the expiry date is invalid, return null.
     * Uses Luxon's DateTime and Duration for calculation.
     */
    featureSetExpirationDateTimeCountdown(): { inMonths: number; inDays: number } | null {
      if (!this.featureSetExpirationDateTime) return null;
      // Always refer to UTC Midnight for expiration comparison
      const today = DateTime.utc().startOf('day');
      const expiryDate = this.featureSetExpirationDateTime.startOf('day');
      const diff = expiryDate.diff(today, ['months', 'days']);
      const inMonths = Math.floor(diff.months);
      const inDays = Math.floor(diff.days);
      return { inMonths, inDays };
    },

    /**
     * Get the feature sets plan type quotas (used and max)
     * for each feature set type.
     */
    featureSetPlanTypeQuotas(): Record<keyof typeof FEATURE_SET_TYPES, GroupPlanQuota> | null {
      if (!this.groupInfo) return null;

      const featureSetTypeCapacities = this.groupInfo?.featureSets?.capacities;
      return Object.keys(FEATURE_SET_TYPES).reduce((acc, key) => {
        const featureSetTypeKey = key as keyof typeof FEATURE_SET_TYPES;
        const featureSetTypeCapacity = featureSetTypeCapacities?.[featureSetTypeKey];
        const used = featureSetTypeCapacity?.used ?? 0;
        const max = featureSetTypeCapacity?.max ?? 0;

        acc[featureSetTypeKey] = { used, max };
        return acc;
      }, {} as Record<keyof typeof FEATURE_SET_TYPES, GroupPlanQuota>);
    },
    /**
     * Check if the Feature Sets subscription is expired based on the
     * expiration date.
     * @returns {boolean} True if the subscription is expired,
     *  false otherwise.
     */
    isFeatureSetsSubscriptionExpired(): boolean {
      if (!this.featureSetExpirationDateTime?.isValid) return false;
      // Always refer to UTC Midnight for expiration comparison
      const today = DateTime.utc().startOf('day');
      const expiryDate = this.featureSetExpirationDateTime;

      return expiryDate < today;
    },
    /**
     * Check if the Feature Sets plan is suspended.
     * @returns {boolean} True if the plan is suspended,
     * false otherwise.
     */
    isFeatureSetsSuspended: (state): boolean => {
      return !!state.groupInfo?.featureSets?.suspended;
    },
    /**
     * Check if the Arya and Feature Sets plan expirations are aligned.
     */
    isActivePlansExpirationAligned(): boolean {
      const aryaExpiration = this.aryaPlanExpirationDateTime;
      const featureSetsExpiration = this.featureSetExpirationDateTime;
      if (!aryaExpiration?.isValid || !featureSetsExpiration?.isValid) {
        return false;
      }

      return aryaExpiration.toUTC().equals(featureSetsExpiration.toUTC());
    },
    /**
     * Getters for aligned expiration date information between
     * Arya and Feature Sets plans.
     */
    isActivePlansAlignedAndExpired(): boolean {
      return this.isActivePlansExpirationAligned && (this.isAryaPlanExpired || this.isFeatureSetsSubscriptionExpired);
    },
    activePlansAlignedExpirationDate(): string | null {
      return this.isActivePlansExpirationAligned
        ? this.aryaPlanExpirationDate ?? this.featureSetExpirationDate
        : null;
    },
    activePlansAlignedExpirationDateTime(): DateTime | null {
      return this.isActivePlansExpirationAligned
        ? this.aryaPlanExpirationDateTime ?? this.featureSetExpirationDateTime
        : null;
    },
    activePlansAlignedExpirationDateMedFormat(): string | null {
      return this.isActivePlansExpirationAligned
        ? this.aryaPlanExpirationDateMedFormat ?? this.featureSetExpirationDateMedFormat
        : null;
    },
    activePlansAlignedExpirationDateTimeCountdown(): { inMonths: number; inDays: number } | null {
      return this.isActivePlansExpirationAligned
        ? this.aryaPlanExpirationDateTimeCountdown ?? this.featureSetExpirationDateTimeCountdown
        : null;
    },
    searchableUsers(state): SearchableGroupUser[] {
      return state.users.map((user) => ({
        ...user,
        fullName: `${user.firstName} ${user.lastName}`.trim()
      }));
    },
    isARYADemoExtensionAllowed(): boolean {
      if (!this.isAryaPlanEnterpriseDemo) return false;

      return (this.groupInfo?.activePlan?.aryaDemoExtensionTimestamps ?? []).length < 1;
    }
  },
  actions: {
    async loadGroupUsers() {
      const productApp = useAppConfig().app as APP;
      this.users = [];
      this.isLoadingUsers = true;

      const { success, error, response } = await handleRequest(async () => {
        let product: components['parameters']['groupUserListProductFilter'] = PRODUCT_ARYA;

        if (productApp === APP.HUB) {
          product = 'all';
        }

        const result = await useGroupApi().getGroupUsers(product);
        const responseData = result?.data;

        return {
          users: (responseData?.users || []) as GroupUser[],
          allowableRights: responseData?.allowableRights ?? [],
          allowableProducts: responseData?.allowableProducts ?? []
        };
      });

      this.isLoadingUsers = false;

      if (success) {
        this.users = response?.users ?? [];
        this.allowableRights = response?.allowableRights ?? [];
        this.allowableProducts = response?.allowableProducts ?? [];
      }

      return { success, error, response };
    },
    async loadGroupInfo() {
      this.isLoadingGroupInfo = true;

      const { success, error, response } = await handleRequest(async () => {
        const result = await useGroupApi().getGroupInfo();
        const responseData = result?.data;

        return { groupInfo: responseData };
      });

      this.isLoadingGroupInfo = false;

      if (success) {
        this.groupInfo = response?.groupInfo ?? null;
      }

      return { success, error, response };
    },
    async updateGroupInfo(payload: GroupSchema['UpdateGroupInfoRequest']) {
      this.isLoadingGroupInfo = true;

      const { success, error } = await handleRequest(async () => {
        await useGroupApi().putGroupInfo(payload);
      });

      this.isLoadingGroupInfo = false;
      if (success && this.groupInfo) {
        this.groupInfo = {
          ...this.groupInfo,
          accountName: payload.accountName ?? this.groupInfo.accountName,
          address: {
            ...this.groupInfo?.address ?? {},
            ...payload.billing_address ?? {}
          }
        };
      }

      return { success, error };
    },
    async inviteGroupUser(user: GroupSchema['CreateOrInviteUserInput']) {
      const { success, error, response } = await handleRequest(async () => {
        await useGroupApi().postCreateOrInviteUser(user);

        // TODO: This is only temporary to get the added userId, but
        // ultimately we want the API to return it for us.
        const loadUsersResponse = await this.loadGroupUsers();
        if (loadUsersResponse?.response?.users?.length) {
          const newUser = loadUsersResponse.response.users.find((item) => item.email === user.username);
          return { userId: newUser?.userId };
        }

        return undefined;
      });

      if (error?.name === 'ExceededAllowableUsersError') {
        return { success, error: error.name, response };
      } else if (error?.translatedMessage === 'errors.existing.userInAccount') {
        return { success, error: error.translatedMessage, response };
      }

      return { success, response };
    },
    async addOrRemoveProductsForGroupUser(
      userId: string,
      products: { initialProducts: Product[]; updatedProducts: Product[] }) {
      const addedProducts = products.updatedProducts.filter((product) => !products.initialProducts.includes(product));
      const removedProducts = products.initialProducts.filter((product) => !products.updatedProducts.includes(product));

      if (!addedProducts.length && !removedProducts.length) return null; // No changes, no need to call API

      const payload: GroupSchema['ModifyUserProductsInput'] = {};

      if (addedProducts.length) payload.add = addedProducts;
      if (removedProducts.length) payload.remove = removedProducts;

      const { success, error } = await handleRequest(async () => {
        await useUserApi().patchModifyUserProducts(userId, payload);
      });

      return { success, error };
    },
    async addProductForGroupUser(userId: string, productToAssign: Product) {
      const { success, error } = await handleRequest(async () => {
        await useUserApi().patchModifyUserProducts(userId, { add: [productToAssign] });
      });

      return { success, error };
    },
    async modifyProductForGroupAccount(product: Product, action: 'add' | 'remove') {
      let { success, error } = await handleRequest(async () => {
        await useGroupApi().patchModifyAccountProducts({ [action]: [product] });
      });

      if (success) {
        const response = await useUserStore().getEnabledProducts();
        success = response.success;
        error = response.error;
      }

      return { success, error };
    },
    async addProductForGroupAccount(product: Product) {
      return await this.modifyProductForGroupAccount(product, 'add');
    },
    async removeProductFromGroupAccount(product: Product) {
      return await this.modifyProductForGroupAccount(product, 'remove');
    },
    async modifyRightsForGroupUser(
      userId: string,
      initialUserRights: GroupUserRights | string[],
      updatedUserRights: GroupUserRights | string[]
    ) {
      if (!initialUserRights?.length && !updatedUserRights?.length) {
        // No changes have been made, return undefined
        return;
      }

      const oldRights = (initialUserRights || []) as GroupUserRights;
      const newRights = (updatedUserRights || []) as GroupUserRights;

      const { success, error, response } = await handleRequest(async () => {
        const addedRights: GroupUserRights = [];
        const removedRights: GroupUserRights = [];

        // Filter added rights from new to old
        if (newRights?.length) {
          addedRights.push(...newRights.filter((right) => !oldRights.includes(right)));
        }

        // Filter removed rights from old to new
        if (oldRights?.length) {
          removedRights.push(...oldRights.filter((right) => !newRights.includes(right)));
        }

        if (!addedRights?.length && !removedRights?.length) {
          // No changes have been made, return as success with no response
          return;
        }

        const payload: GroupSchema['ModifyUserRightsInput'] = {};
        if (addedRights?.length) payload.add = addedRights;
        if (removedRights?.length) payload.remove = removedRights;

        await useGroupApi().patchModifyGroupUserRights(userId, {
          add: addedRights ?? undefined,
          remove: removedRights ?? undefined
        });

        return {
          userId,
          initialUserRights,
          updatedUserRights
        };
      });

      if (success && !response) {
        // No changes have been made, return undefined
        return;
      }

      return { success, error, response };
    },
    async deleteUserFromGroup(userId: string) {
      const { success, error } = await handleRequest(async () => {
        return await useGroupApi().deleteGroupUser(userId);
      });

      return { success, error };
    },
    async deleteUserMeFromGroup() {
      const { success, error } = await handleRequest(async () => {
        return await useGroupApi().deleteGroupUser('me');
      });

      return { success, error };
    },
    async deleteGroup() {
      const { success, error } = await handleRequest(async () => {
        return await useGroupApi().deleteGroup();
      });

      return { success, error };
    },
    async generateNewApiKey() {
      const { success, error, response } = await handleRequest(async () => {
        const result = await useGroupApi().postGroupApiKey();
        return result?.data;
      });

      // Update groupInfo.hasAPIKey if API key generation was successful
      if (success && this.groupInfo) {
        this.groupInfo.hasAPIKey = true;
      }

      return { success, error, response };
    },
    /**
     * This method is specifically for creating a new HUB
     * account, which automatically enables the 'assets'
     * product.
     * @param request - The account creation request payload.
     * @returns An object containing the success status,
     * error (if any), and response with created `groupId`.
     */
    async createNewHUBAccount(request: GroupSchema['CreateAccountRequest']) {
      const { success, error, response } = await handleRequest(async () => {
        const result = await useGroupApi().postCreateAccount({
          ...request,
          // Strictly assign only assets product for HUB account.
          products: [PRODUCT_ASSETS]
        });

        return result?.data;
      });

      return { success, error, response };
    },
    /**
     * This method is specifically for creating a new ARYA
     * account, which automatically enables the 'arya'
     * product (with ARYA Enterprise Trial subscription
     * automatically created on the account).
     * @param request - The account creation request payload.
     * @returns An object containing the success status,
     * error (if any), and response with created `groupId`.
     */
    async createNewARYAAccount(request: GroupSchema['CreateAccountRequest']) {
      const { success, error, response } = await handleRequest(async () => {
        const result = await useGroupApi().postCreateAccount({
          ...request,
          // Strictly assign only arya and assets product for ARYA account.
          products: [PRODUCT_ARYA, PRODUCT_ASSETS]
        });

        return result?.data;
      });

      return { success, error, response };
    },
    /**
     * Silently sets accountName from companyName when both
     * companyName and countryCode are present.
     */
    async trySilentAccountNamePrefill() {
      if (
        !this.groupInfo || this.groupInfo.accountName
        || !this.groupInfo.address?.companyName
        || !this.groupInfo.address?.countryCode
      ) {
        return;
      }

      this.isSilentUpdatingAccountName = true;
      const { error } = await this.updateGroupInfo({
        accountName: this.groupInfo.address?.companyName
      });
      this.isSilentUpdatingAccountName = false;

      if (error) {
        console.error('Failed to set company name as account name', error);
      }
    },
    /**
     * This method is specifically for extending the ARYA Enterprise Demo
     * subscription expiry date by 30 days. It can only be called once
     * per account.
     * @returns An object containing the success status,
     * error (if any), and response with updated `expiryDate`.
     */
    async extendARYADemoExpiry() {
      const { success, error, response } = await handleRequest(async () => {
        const result = await useGroupApi().postExtendPlanExpiry();
        return result?.data;
      });

      if (success && this.groupInfo?.activePlan) {
        // Update the active plan's expiry date with the new
        // expiry date from the response
        this.groupInfo.activePlan.expiryDate = response?.expiryDate ?? this.groupInfo.activePlan.expiryDate;
      }

      return { success, error, response };
    }
  }
});
